// SPDX-License-Identifier: AGPL-3.0-or-later

// Fetches the server-side numbers behind progression badges (game time, games played, boost start)
// and combines them with what the client already knows (account age, Pro start).

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {type AchievementProgress, computeAchievements} from '@app/features/user/achievements/AchievementCatalog';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import type {UserAchievementStatsResponse} from '@fluxer/schema/src/domains/user/GameHistorySchemas';
import {UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import {useEffect, useMemo, useState} from 'react';

const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
	fetchedAt: number;
	data: UserAchievementStatsResponse | null;
	promise: Promise<UserAchievementStatsResponse | null> | null;
}

const cache = new Map<string, CacheEntry>();

function fetchStats(userId: string): Promise<UserAchievementStatsResponse | null> {
	const entry = cache.get(userId);
	if (entry?.promise) return entry.promise;
	if (entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS) return Promise.resolve(entry.data);
	const promise = http
		.get<UserAchievementStatsResponse>(Endpoints.USER_ACHIEVEMENT_STATS(userId))
		.then((response) => response.body)
		// Hidden profile, older API or network trouble: show only client-derived badges.
		.catch(() => null)
		.then((data) => {
			cache.set(userId, {fetchedAt: Date.now(), data, promise: null});
			return data;
		});
	cache.set(userId, {fetchedAt: entry?.fetchedAt ?? 0, data: entry?.data ?? null, promise});
	return promise;
}

function readCached(userId: string): UserAchievementStatsResponse | null {
	return cache.get(userId)?.data ?? null;
}

export function useUserAchievements(user: User, profile: Profile | null): Array<AchievementProgress> {
	const [stats, setStats] = useState<UserAchievementStatsResponse | null>(() => readCached(user.id));
	const isBot = user.bot;
	useEffect(() => {
		if (isBot) return;
		let cancelled = false;
		void fetchStats(user.id).then((data) => {
			if (!cancelled) setStats(data);
		});
		return () => {
			cancelled = true;
		};
	}, [user.id, isBot]);
	// Profiles carry Pro data for others; for yourself the user model already has it.
	const premiumType = profile?.premiumType ?? user.premiumType ?? null;
	const isPro = premiumType != null && premiumType !== UserPremiumTypes.NONE;
	const premiumSince = isPro ? (profile?.premiumSince ?? user.premiumSince ?? null) : null;
	return useMemo(() => {
		if (isBot) return [];
		return computeAchievements({
			accountCreatedAt: user.createdAt,
			premiumSince,
			playMs: stats?.play_ms ?? null,
			gamesPlayed: stats?.games_played ?? null,
			boostingSince: stats?.boosting_since ? new Date(stats.boosting_since) : null,
		});
	}, [isBot, user.createdAt, premiumSince, stats]);
}
